import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikProps } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { Tab, Tabs } from 'src/components/Tabs';
import { useSession } from 'src/contexts/SessionContext';
import { Platform, PlatformTab } from 'src/models/platform/_types';
import { updatePlatform } from 'src/models/platform/platform';
import {
  getCombinedClientNavItems,
  isHQPlatform,
  supportsV2Navigation,
} from 'src/models/platform/utils';
import { CustomAppDrawerEditor } from 'src/pages/Admin/Platform/Details/CustomAppDrawerEditor';
import { CustomAppNavigationEditor } from 'src/pages/Admin/Platform/Details/CustomAppNavigationEditor';
import { CustomProfileMenuEditor } from 'src/pages/Admin/Platform/Details/CustomProfileMenuEditor';
import { PageEditor } from 'src/pages/Admin/Platform/Details/PageEditor';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';
import toast from 'src/utils/toast';
import * as Yup from 'yup';

import { MenuSection } from './MenuSection';

interface EditNavigationDialogProps {
  open: boolean;
  onClose: () => void;
  asPage?: boolean;
}

type MenuSection =
  | 'trainer'
  | 'account'
  | 'client'
  | 'clientDashboard'
  | 'clientAccount'
  | 'appNavigation'
  | 'termsOfService'
  | 'privacyPolicy';

interface FormValues {
  platformTabs: {
    trainerItems: PlatformTab[];
    accountItems: PlatformTab[];
    clientItems: PlatformTab[];
    clientDashboardItems: PlatformTab[];
    clientAccountItems: PlatformTab[];
  };
  disableNavigationClientManagementTabs: boolean;
  disableNavigationClientDashboardTabs: boolean;
  disableNavigationClientAccountTabs: boolean;
  disableNavigationTrainerDashboardTabs: boolean;
  disableNavigationTrainerAccountTabs: boolean;
  disableNavigationMainNavTabs: boolean;
}

export const EditNavigationDialog: React.FC<EditNavigationDialogProps> = ({
  open,
  onClose,
  asPage = false,
}) => {
  const queryClient = useQueryClient();
  const { session, refreshSessionAndUser, user } = useSession();
  const {
    canManageAppNavigation,
    canManagePages,
    canManageTrainerDashboardNavigation,
    canManageTrainerAccountNavigation,
    canManageClientManagementNavigation,
    canManageClientDashboardNavigation,
    canManageClientAccountNavigation,
  } = usePermissionCheck();
  const [activeTab, setActiveTab] = useState<MenuSection>('trainer');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const { t: tDialog } = useTranslation('translation', {
    keyPrefix: 'editNavigationDialog',
  });
  const formikRef = useRef<FormikProps<FormValues>>(null);

  const isHQ = isHQPlatform(session);
  // Refresh page data when tab is selected
  useEffect(() => {
    if (activeTab === 'termsOfService') {
      queryClient.invalidateQueries({
        queryKey: ['pages', 'terms-of-service'],
      });
    } else if (activeTab === 'privacyPolicy') {
      queryClient.invalidateQueries({
        queryKey: ['pages', 'privacy-policy'],
      });
    }
  }, [activeTab, queryClient]);

  const sortItems = (items: PlatformTab[]) => {
    return [...items];
  };

  const tabs = isHQ ? session?.subPlatformTabs : session?.platformTabs;

  const computeFormValues = useCallback(
    (tabsData: any, settings: any): FormValues => {
      return {
        platformTabs: {
          trainerItems: sortItems(tabsData?.trainerDashboard || []),
          accountItems: sortItems(tabsData?.trainerAccount || []),
          clientItems: sortItems(tabsData?.clientManagement || []),
          clientDashboardItems: sortItems(getCombinedClientNavItems(tabsData)),
          clientAccountItems: sortItems(tabsData?.clientAccount || []),
        },
        disableNavigationClientManagementTabs:
          settings?.disableNavigationClientManagementTabs || false,
        disableNavigationClientDashboardTabs:
          settings?.disableNavigationClientDashboardTabs || false,
        disableNavigationClientAccountTabs:
          settings?.disableNavigationClientAccountTabs || false,
        disableNavigationTrainerDashboardTabs:
          settings?.disableNavigationTrainerDashboardTabs || false,
        disableNavigationTrainerAccountTabs:
          settings?.disableNavigationTrainerAccountTabs || false,
        disableNavigationMainNavTabs:
          settings?.disableNavigationMainNavTabs || false,
      };
    },
    []
  );

  const [formValues, setFormValues] = useState<FormValues>(
    computeFormValues(tabs, session)
  );

  // Keep formValues in sync if tabs change (e.g. after session/setting update)
  useEffect(() => {
    setFormValues(computeFormValues(tabs, session));
  }, [computeFormValues, session, tabs]);

  const updatePlatformMutation = useMutation({
    mutationFn: (values: FormValues): Promise<Platform> => {
      const platformTabs = values.platformTabs;
      const dataLoad = {
        ...tabs,
        trainerDashboard: platformTabs.trainerItems,
        trainerAccount: platformTabs.accountItems,
        clientManagement: platformTabs.clientItems,
        clientDashboard: platformTabs.clientDashboardItems,
        clientAccount: platformTabs.clientAccountItems,
        mainNav: [],
      };

      const updateData = isHQ
        ? {
            subPlatformTabs: dataLoad,
            disableNavigationClientManagementTabs:
              values.disableNavigationClientManagementTabs,
            disableNavigationClientDashboardTabs:
              values.disableNavigationClientDashboardTabs,
            disableNavigationClientAccountTabs:
              values.disableNavigationClientAccountTabs,
            disableNavigationTrainerDashboardTabs:
              values.disableNavigationTrainerDashboardTabs,
            disableNavigationTrainerAccountTabs:
              values.disableNavigationTrainerAccountTabs,
            disableNavigationMainNavTabs: values.disableNavigationMainNavTabs,
          }
        : {
            platformTabs: dataLoad,
          };

      return updatePlatform(session.id, updateData, false, user);
    },

    onSuccess: async (data: Platform) => {
      await refreshSessionAndUser();
      if (isHQ) {
        const newTabs = data.subPlatformTabs;
        setFormValues(computeFormValues(newTabs, data));
      } else {
        const newTabs = data.platformTabs;
        setFormValues(computeFormValues(newTabs, session));
      }
      toast.success('Navigation items updated successfully');
      onClose();
    },

    onError: () => {
      toast.error('Failed to update navigation items');
    },
  });

  const handleTabChange = (newTab: MenuSection) => {
    // Force refresh the queries when tab changes to ensure fresh data
    if (newTab === 'termsOfService') {
      queryClient.removeQueries({
        queryKey: ['pages', 'terms-of-service'],
      });
    } else if (newTab === 'privacyPolicy') {
      queryClient.removeQueries({
        queryKey: ['pages', 'privacy-policy'],
      });
    }

    setActiveTab(newTab);
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={formValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={(values) => {
        updatePlatformMutation.mutate(values);
      }}
    >
      {({ values, setFieldValue, handleSubmit, resetForm }) => {
        const content = (
          <Form>
            <Box sx={{ width: '100%', mt: 1 }}>
              <Tabs
                value={activeTab}
                onChange={(_, value) => handleTabChange(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  mb: 2,
                  '.MuiTabs-scrollButtons.Mui-disabled': {
                    width: 0,
                    opacity: 0,
                  },
                }}
              >
                {canManageTrainerDashboardNavigation && (
                  <Tab
                    label={tDialog('tabs.trainerDashboard')}
                    value="trainer"
                  />
                )}
                {canManageTrainerAccountNavigation && (
                  <Tab label={tDialog('tabs.trainerAccount')} value="account" />
                )}
                {canManageClientManagementNavigation && (
                  <Tab
                    label={tDialog('tabs.clientManagement')}
                    value="client"
                  />
                )}
                {canManageClientDashboardNavigation && (
                  <Tab
                    label={tDialog('tabs.clientDashboard')}
                    value="clientDashboard"
                  />
                )}
                {canManageClientAccountNavigation && (
                  <Tab
                    label={tDialog('tabs.clientAccount')}
                    value="clientAccount"
                  />
                )}
                {canManageAppNavigation && (
                  <Tab
                    label={tDialog('tabs.appNavigation')}
                    value="appNavigation"
                  />
                )}
                {canManagePages && !isHQ && (
                  <Tab
                    label={tDialog('tabs.termsOfService')}
                    value="termsOfService"
                  />
                )}
                {canManagePages && !isHQ && (
                  <Tab
                    label={tDialog('tabs.privacyPolicy')}
                    value="privacyPolicy"
                  />
                )}
              </Tabs>

              {activeTab === 'appNavigation' ? (
                <Stack spacing={4}>
                  <CustomAppDrawerEditor
                    platform={session}
                    defaultExpanded={!supportsV2Navigation(session)}
                  />
                  {supportsV2Navigation(session) && (
                    <>
                      <CustomAppNavigationEditor platform={session} />
                      <CustomProfileMenuEditor platform={session} />
                    </>
                  )}
                </Stack>
              ) : activeTab === 'termsOfService' ? (
                <PageEditor
                  platform={session}
                  cachedSlug="terms-of-service"
                  pageTitle={tDialog('tabs.termsOfService')}
                />
              ) : activeTab === 'privacyPolicy' ? (
                <PageEditor
                  platform={session}
                  cachedSlug="privacy-policy"
                  pageTitle={tDialog('tabs.privacyPolicy')}
                />
              ) : (
                <MenuContent
                  section={activeTab}
                  values={values}
                  setFieldValue={setFieldValue}
                  editingItemId={editingItemId}
                  setEditingItemId={setEditingItemId}
                  isLoading={updatePlatformMutation.isPending}
                  isHQ={isHQ}
                />
              )}
            </Box>
          </Form>
        );

        if (asPage) {
          // Render content directly, not in a Dialog, but add Save and Cancel buttons
          return (
            <>
              {content}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2,
                  mt: 3,
                }}
              >
                <Button
                  onClick={() => resetForm()}
                  disabled={updatePlatformMutation.isPending}
                  sx={{ minWidth: 100 }}
                  variant="bordered"
                >
                  {tDialog('cancel')}
                </Button>
                <Button
                  onClick={() => handleSubmit()}
                  disabled={updatePlatformMutation.isPending}
                  sx={{ minWidth: 100 }}
                  variant="prominent"
                  color="primary"
                >
                  {tDialog('save')}
                </Button>
              </Box>
            </>
          );
        }

        // Render content inside a Dialog
        return (
          <Dialog
            open={open}
            onClose={onClose}
            title={tDialog('customizeNavigation')}
            fullWidth
            fullScreen={true}
            actions={[
              {
                label: tDialog('cancel'),
                onClick: onClose,
              },
              {
                label: tDialog('save'),
                variant: 'prominent',
                onClick: () => handleSubmit(),
                disabled: updatePlatformMutation.isPending,
              },
            ]}
          >
            {content}
          </Dialog>
        );
      }}
    </Formik>
  );
};

interface MenuContentProps {
  section: MenuSection;
  values: FormValues;
  setFieldValue: (field: string, value: any) => void;
  editingItemId: string | null;
  setEditingItemId: (id: string | null) => void;
  isLoading: boolean;
  isHQ: boolean;
}

const MenuContent: React.FC<MenuContentProps> = ({
  section,
  values,
  setFieldValue,
  editingItemId,
  setEditingItemId,
  isLoading,
  isHQ,
}) => {
  const { t: tDialog } = useTranslation('translation', {
    keyPrefix: 'editNavigationDialog',
  });

  const getMenuProps = () => {
    switch (section) {
      case 'trainer':
        return {
          title: tDialog('menus.trainerDashboard'),
          items: values.platformTabs.trainerItems,
          setItems: (items: PlatformTab[]) =>
            setFieldValue('platformTabs.trainerItems', items),
          setDisableTab: (value: boolean) =>
            setFieldValue('disableNavigationTrainerDashboardTabs', value),
          disableTab: values.disableNavigationTrainerDashboardTabs,
          canDisableTab: isHQ,
          tabArrayKey: 'trainerItems',
        };
      case 'account':
        return {
          title: tDialog('menus.trainerAccount'),
          items: values.platformTabs.accountItems,
          setItems: (items: PlatformTab[]) =>
            setFieldValue('platformTabs.accountItems', items),
          setDisableTab: (value: boolean) =>
            setFieldValue('disableNavigationClientAccountTabs', value),
          disableTab: values.disableNavigationClientAccountTabs,
          canDisableTab: isHQ,
          tabArrayKey: 'accountItems',
        };
      case 'client':
        return {
          title: tDialog('menus.clientManagement'),
          items: values.platformTabs.clientItems,
          setItems: (items: PlatformTab[]) =>
            setFieldValue('platformTabs.clientItems', items),
          setDisableTab: (value: boolean) =>
            setFieldValue('disableNavigationClientManagementTabs', value),
          disableTab: values.disableNavigationClientManagementTabs,
          canDisableTab: isHQ,
          tabArrayKey: 'clientItems',
        };
      case 'clientDashboard':
        return {
          title: tDialog('menus.clientDashboard'),
          items: values.platformTabs.clientDashboardItems,
          setItems: (items: PlatformTab[]) =>
            setFieldValue('platformTabs.clientDashboardItems', items),
          setDisableTab: (value: boolean) =>
            setFieldValue('disableNavigationClientDashboardTabs', value),
          disableTab: values.disableNavigationClientDashboardTabs,
          canDisableTab: isHQ,
          tabArrayKey: 'clientDashboardItems',
        };
      case 'clientAccount':
        return {
          title: tDialog('menus.clientAccount'),
          items: values.platformTabs.clientAccountItems,
          setItems: (items: PlatformTab[]) => {
            setFieldValue('platformTabs.clientAccountItems', items);
          },
          setDisableTab: (value: boolean) =>
            setFieldValue('disableNavigationClientAccountTabs', value),
          disableTab: values.disableNavigationClientAccountTabs,
          canDisableTab: isHQ,
          tabArrayKey: 'clientAccountItems',
        };
      case 'appNavigation':
        return {
          title: tDialog('menus.appNavigation'),
          items: [],
          setItems: (/* items: PlatformTab[] */) => {
            // App navigation is handled by separate components
          },
          disableTab: false,
          canDisableTab: false,
          setDisableTab: () => {
            // Not applicable
          },
          tabArrayKey: null,
        };
      case 'termsOfService':
        return {
          title: tDialog('tabs.termsOfService'),
          items: [],
          setItems: (/* items: PlatformTab[] */) => {
            // Terms of service is handled by separate components
          },
          disableTab: false,
          canDisableTab: false,
          setDisableTab: () => {
            // Not applicable
          },
          tabArrayKey: null,
        };
      case 'privacyPolicy':
        return {
          title: tDialog('tabs.privacyPolicy'),
          items: [],
          setItems: (/* items: PlatformTab[] */) => {
            // Privacy policy is handled by separate components
          },
          disableTab: false,
          canDisableTab: false,
          setDisableTab: () => {
            // Not applicable
          },
          tabArrayKey: null,
        };
    }
  };

  const menuProps = getMenuProps();
  return (
    <MenuSection
      {...menuProps}
      editingItemId={editingItemId}
      setEditingItemId={setEditingItemId}
      isLoading={isLoading}
    />
  );
};

const validationSchema = Yup.object().shape({
  trainerItems: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Name is required'),
    })
  ),
  accountItems: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Name is required'),
    })
  ),
  clientItems: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Name is required'),
    })
  ),
  clientDashboardItems: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Name is required'),
    })
  ),
  clientAccountItems: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Name is required'),
    })
  ),
});
